import React, { useState, useEffect, useMemo } from 'react';
import { DBTable } from '../components/DBTable';
import { usePermission } from '../hooks/usePermission';
import { AccessLevel } from '../types';
import { useNavigate } from 'react-router-dom';
import { Users, User, ChevronDown } from 'lucide-react';
import { SignedImage } from '../components/SignedImage';
import { getPersonPhotoPath, calculateAge } from '../lib/utils';
import { supabase } from '../lib/supabase';

const Persone: React.FC = () => {
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const [tipologie, setTipologie] = useState<{ id: number, descrizione: string }[]>([]);
  const [selectedTipologia, setSelectedTipologia] = useState<string>('Socio CPGEV');

  const canWrite = hasPermission('app.segreteria.anagrafica.persone', AccessLevel.rw);
  const canRead = hasPermission('app.segreteria.anagrafica', AccessLevel.ro);

  useEffect(() => {
    const fetchTipologie = async () => {
      try {
        const { data, error } = await supabase
          .from('cpgev_tipologia_anagrafiche')
          .select('id, descrizione')
          .order('id', { ascending: true });
        
        if (error) throw error;
        if (data) {
          setTipologie(data);
          // Imposta il valore iniziale basato sull'ID 1 se presente
          const socio = data.find(t => t.id === 1);
          if (socio) {
            setSelectedTipologia(socio.descrizione);
          }
        }
      } catch (err) {
        console.error('Error fetching tipologie:', err);
      }
    };

    fetchTipologie();
  }, []);

  if (!canRead) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl border shadow-sm">
        <h2 className="text-xl font-bold text-red-500">Accesso Negato</h2>
        <p className="text-slate-500">Non hai i permessi necessari per visualizzare questa sezione.</p>
      </div>
    );
  }

  const handleRowDoubleClick = (row: any) => {
    navigate(`/segreteria/anagrafica/persone/${row.id}?action=view`);
  };

  const columns = React.useMemo(() => [
    'id', 'foto', 'cognome', 'nome', 'sesso', 'data_nascita', 'comune_nascita',
    'provincia_nascita', 'nazione_nascita', 'ultimo_accesso', 'tipologia_anagrafica',
    'codice_fiscale', 'partita_iva', 'regione_nascita', 'titolo_onorifico',
    'tipo_scuola', 'titolo_studio', 'note', 'data_inserimento', 'inserito_da',
    'data_aggiornamento', 'modificato_da'
  ], []);

  const virtualColumns = React.useMemo(() => ['foto'], []);

  const columnLabels = React.useMemo(() => ({
    id: 'ID',
    foto: 'Foto',
    cognome: 'Cognome',
    nome: 'Nome',
    ultimo_accesso: 'Ult. accesso',
    tipologia_anagrafica: 'Tipologia',
    codice_fiscale: 'C.F.',
    partita_iva: 'P.IVA',
    sesso: 'Sesso',
    data_nascita: 'Data nascita',
    comune_nascita: 'Comune nascita',
    provincia_nascita: 'Provincia nascita',
    regione_nascita: 'Regione nascita',
    nazione_nascita: 'Nazione nascita',
    titolo_onorifico: 'Titolo onorifico',
    tipo_scuola: 'Scuola',
    titolo_studio: 'Titolo di studio',
    note: 'Note',
    data_inserimento: 'Data inserimento',
    inserito_da: 'Inserito da',
    data_aggiornamento: 'Data agg.',
    modificato_da: 'Modificato da'
  }), []);

  const renderers = React.useMemo(() => ({
    foto: (val: any, row: any) => (
      <div className="flex items-center justify-center py-1">
        <SignedImage 
          path={getPersonPhotoPath(row.cognome, row.nome, true)}
          className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm"
          alt={`${row.nome} ${row.cognome}`}
          fallback={
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
              <User className="w-5 h-5 text-slate-400" />
            </div>
          }
        />
      </div>
    ),
    data_nascita: (val: any) => {
      if (!val) return <span className="text-slate-300">-</span>;
      const age = calculateAge(val);
      return (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span>{val}</span>
          {age !== null && (
            <span
              className="text-[10px] font-black bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-md border border-slate-200"
              title={`Età calcolata ad oggi: ${age} anni`}
            >
              {age}a
            </span>
          )}
        </div>
      );
    }
  }), []);

  // Filtro per tipologia selezionata. Supporta "Tutte" se selectedTipologia è 'all'
  const whereClause = useMemo(() => {
    if (!selectedTipologia || selectedTipologia === 'all') return '';
    const escapedValue = selectedTipologia.replace(/'/g, "''");
    return `tipologia_anagrafica = '${escapedValue}'`;
  }, [selectedTipologia]);

  const headerExtras = useMemo(() => (
    <div className="flex items-center gap-3">
      <div className="relative group">
        <select
          value={selectedTipologia}
          onChange={(e) => setSelectedTipologia(e.target.value)}
          className="appearance-none pl-4 pr-10 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm cursor-pointer min-w-[220px]"
        >
          <option value="all">Filtra per Tipologia: Tutte</option>
          {tipologie.map(t => (
            <option key={t.id} value={t.descrizione}>{t.id} - {t.descrizione}</option>
          ))}
          {tipologie.length === 0 && selectedTipologia === 'Socio CPGEV' && (
            <option value="Socio CPGEV">1 - Socio CPGEV</option>
          )}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary pointer-events-none transition-colors" />
      </div>
    </div>
  ), [selectedTipologia, tipologie]);

  const handleRefresh = React.useCallback(() => {
    // Svuota la cache delle immagini firmate per forzarne la rigenerazione
    Object.keys(localStorage)
      .filter(key => key.startsWith('gesvol_cache_signed_url_'))
      .forEach(key => localStorage.removeItem(key));
  }, []);

  return (
      <DBTable 
        title="Persone"
        tableName="cpgev_anagrafica_view"
        schema="public"
        whereClause={whereClause}
        headerExtras={headerExtras}
        columns={columns}
        virtualColumns={virtualColumns}
        pinnedColumns={5}
        gridAxis="both"
        sortable={true}
        searchable={true}
        searchPlaceholder="Cerca persona o età (es. > 65, < 35, <> 80)..."
        filterable={true}
        selectable={true}
        limit={9999}
        pageSize={1000}
        onRowDoubleClick={handleRowDoubleClick}
        onView={(row) => navigate(`/segreteria/anagrafica/persone/${row.id}?action=view`)}
        onAdd={canWrite ? () => navigate('/segreteria/anagrafica/persone/new?action=add') : undefined}
        onEdit={canWrite ? (row) => navigate(`/segreteria/anagrafica/persone/${row.id}?action=edit`) : undefined}
        addLabel="Aggiungi persona"
        renderers={renderers}
        columnLabels={columnLabels}
        forceGeo={true}
        onRefresh={handleRefresh}
      />
  );
};

export default Persone;
